import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Card, CardContent,
  CircularProgress, Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import * as eqpApi from '../../lib/api/eqp';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const EqpPlanTab = ({ eqpUuid }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [planMap, setPlanMap] = useState({}); // { '202601': { planHr: 176, planUuid: '...' } }
  const [inputs, setInputs] = useState({});   // { '202601': '176' }
  const [saving, setSaving] = useState(false);

  const loadPlans = useCallback(async () => {
    if (!eqpUuid || !year) return;
    try {
      const res = await eqpApi.getPlanList({ eqpUuid, searchYear: year });
      const map = {};
      (res.data.list || []).forEach((p) => {
        map[p.planYm] = p;
      });
      setPlanMap(map);
      const init = {};
      MONTHS.forEach((m) => {
        const ym = `${year}${String(m).padStart(2, '0')}`;
        init[ym] = map[ym]?.planHr != null ? String(map[ym].planHr) : '';
      });
      setInputs(init);
    } catch {
      enqueueSnackbar('예상 가동시간 조회 실패', { variant: 'error' });
    }
  }, [eqpUuid, year]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleChange = (ym, value) => {
    setInputs((prev) => ({ ...prev, [ym]: value }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    let successCnt = 0;
    let errorCnt = 0;
    for (const m of MONTHS) {
      const ym = `${year}${String(m).padStart(2, '0')}`;
      const val = inputs[ym];
      if (val === '' || val == null) continue;
      const planHr = parseFloat(val);
      if (isNaN(planHr) || planHr < 0) continue;
      try {
        await eqpApi.savePlan({ eqpUuid, planYm: ym, planHr });
        successCnt++;
      } catch {
        errorCnt++;
      }
    }
    setSaving(false);
    if (successCnt > 0) enqueueSnackbar(`${successCnt}개월 저장 완료`, { variant: 'success' });
    if (errorCnt > 0) enqueueSnackbar(`${errorCnt}개월 저장 실패`, { variant: 'error' });
    loadPlans();
  };

  const totalPlanHr = MONTHS.reduce((sum, m) => {
    const ym = `${year}${String(m).padStart(2, '0')}`;
    const v = parseFloat(inputs[ym]);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  return (
    <Box>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          label="연도"
          size="small"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          inputProps={{ maxLength: 4 }}
          sx={{ width: 100 }}
        />
        <Typography variant="body2" color="text.secondary">
          장비별 월별 예상(계획) 가동시간을 입력하면 가동률 계산에 반영됩니다.
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {totalPlanHr > 0 && (
            <Chip
              label={`연간 합계: ${totalPlanHr.toFixed(1)}h`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving}
          >
            전체 저장
          </Button>
        </Box>
      </Box>

      {/* 월별 입력 그리드 */}
      <Grid container spacing={1.5}>
        {MONTHS.map((m) => {
          const ym = `${year}${String(m).padStart(2, '0')}`;
          const saved = planMap[ym];
          return (
            <Grid item xs={6} sm={4} md={3} key={ym}>
              <Card variant="outlined" sx={{ p: 0 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {m}월
                    {saved && (
                      <Chip label="저장됨" size="small" color="success" variant="outlined"
                        sx={{ ml: 1, height: 16, fontSize: 10 }} />
                    )}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    label="예상 가동시간 (h)"
                    value={inputs[ym] ?? ''}
                    onChange={(e) => handleChange(ym, e.target.value)}
                    inputProps={{ min: 0, step: 0.5 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default EqpPlanTab;
